import { FiCalendar, FiFileText, FiRefreshCw, FiTrash2, FiVideo } from "react-icons/fi";
import type { UploadQueueItem } from "../types";
import { formatDuration, formatEta, formatFileSize } from "../utils";

interface UploadCardProps {
  item: UploadQueueItem;
  onChange: (id: string, changes: Partial<UploadQueueItem>) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  busy: boolean;
}

export function UploadCard({ item, onChange, onRemove, onRetry, busy }: UploadCardProps) {
  const locked = item.status === "uploading" || item.status === "success";
  return (
    <article className={`upload-card status-${item.status}`}>
      <div className="upload-card-preview">
        <img src={item.thumbnailPreviewUrl} alt={`Preview for ${item.file.name}`} />
        <strong className="upload-file-size"><small>Clip size</small>{formatFileSize(item.file.size)}</strong>
        <span className="upload-preview-duration">{formatDuration(item.durationSeconds)}</span>
        {item.status === "success" && <i className="upload-success-mark" aria-label="Upload successful">✓</i>}
      </div>
      <div className="upload-card-form">
        <div className="upload-file-name">
          <FiVideo aria-hidden="true" />
          <span title={item.file.name}>{item.file.name}</span>
        </div>
        <label>
          <span>Title <b aria-label="required">*</b></span>
          <input
            value={item.title}
            maxLength={120}
            required
            disabled={locked}
            onChange={(event) => onChange(item.localId, { title: event.target.value, error: undefined })}
          />
        </label>
        <label>
          <span><FiFileText aria-hidden="true" /> Description <small>Optional</small></span>
          <textarea
            value={item.description}
            maxLength={1000}
            rows={2}
            disabled={locked}
            onChange={(event) => onChange(item.localId, { description: event.target.value })}
          />
        </label>
        <label>
          <span><FiCalendar aria-hidden="true" /> Recording date <small>Optional</small></span>
          <input
            type="date"
            value={item.recordingDate}
            disabled={locked}
            onChange={(event) => onChange(item.localId, { recordingDate: event.target.value })}
          />
        </label>
      </div>
      <div className="upload-card-state">
        <div className="upload-status-row">
          <strong>{item.status === "waiting" ? "Waiting" : item.status === "uploading" ? "Uploading" : item.status === "success" ? "Complete" : "Failed"}</strong>
          {item.status === "uploading" && <span>{item.progress}%</span>}
        </div>
        {(item.status === "uploading" || item.status === "success") && (
          <div className="upload-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={item.progress}>
            <i style={{ width: `${item.progress}%` }} />
          </div>
        )}
        {item.status === "uploading" && <small>{formatEta(item.etaSeconds)}</small>}
        {item.error && <p className="upload-error">{item.error}</p>}
        <div className="upload-card-actions">
          {item.status === "failed" && (
            <button type="button" onClick={() => onRetry(item.localId)} disabled={busy}>
              <FiRefreshCw /> Retry
            </button>
          )}
          {(item.status === "waiting" || item.status === "failed" || item.status === "success") && (
            <button className="danger" type="button" onClick={() => onRemove(item.localId)} disabled={busy}>
              <FiTrash2 /> Remove
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
